import React from 'react'
import { useAuth } from '@/context/AuthContext'
import {
  Bell,
  Menu,
  ChevronLeft,
  ChevronDown,
  AlertTriangle
} from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import { AudioPlayer } from '@/components/player/AudioPlayer'
import { GlobalFooterPlayer } from '@/components/player/GlobalFooterPlayer'
import { usePlayerStore } from '@/store/playerStore'
import { TicketNotifications, useUnreadTicketCount } from './TicketNotifications'
import { DeviceLogNotifications, useUnreadDeviceLogCount } from './DeviceLogNotifications'

// Sayfa başlıklarını tanımla
const getPageTitle = (pathname: string): string => {
  const routes: { [key: string]: string } = {
    '/dashboard': 'Dashboard',
    '/companies': 'Firma Yönetimi',
    '/users': 'Kullanıcı Yönetimi',
    '/devices': 'Cihaz Yönetimi',
    '/announcements': 'Anonslar',
    '/ai-announcements/api-settings': 'API Ayarları',
    '/ai-announcements/voice-models': 'Ses Modelleri',
    '/ai-announcements/music-collection': 'Müzik Koleksiyonu',
    '/ai-announcements/user-access': 'Kullanıcı Erişimi',
    '/ai-music/packages': 'AI Müzik Yönetimi',
    '/music/upload': 'Müzik Yükleme',
    '/playlists': 'Playlist Yönetimi',
    '/notices': 'Duyurular',
    '/analytics': 'Analitik',
    '/system-settings': 'Sistem Ayarları',
    '/tickets': 'Destek Talepleri',
  }

  return routes[pathname] || 'Dashboard'
}

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true)
  const [notificationsOpen, setNotificationsOpen] = React.useState(false)
  const [deviceLogNotificationsOpen, setDeviceLogNotificationsOpen] = React.useState(false)
  const [profileOpen, setProfileOpen] = React.useState(false)
  const { logout, user } = useAuth()
  const { currentSong } = usePlayerStore()
  const location = useLocation()
  const pageTitle = getPageTitle(location.pathname)
  const unreadTicketCount = useUnreadTicketCount()
  const unreadDeviceLogCount = useUnreadDeviceLogCount()

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-40 h-screen transition-transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } bg-card border-r border-border w-64 pt-16`}
      >
        <div className="h-full px-3 py-4 overflow-y-auto">
          <Sidebar />
        </div>
      </aside>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border h-16">
        <div className="flex items-center justify-between h-full">
          {/* Logo ve Sidebar Toggle - Sol taraf (sidebar genişliğinde) */}
          <div className={`flex border-r border-border transition-all relative ${sidebarOpen ? 'w-64' : 'w-auto'}`} style={{ height: '64px' }}>
            <div className="pl-4 flex items-center">
              {sidebarOpen ? (
                <div className="flex items-center gap-3">
                  <Link
                    to="/dashboard"
                    className="font-black text-[2.5rem] tracking-tight text-foreground hover:text-foreground/80 transition-colors"
                    style={{ lineHeight: '1', marginTop: '-8px' }}
                    aria-label="Veeq Ana Sayfa"
                  >
                    veeq
                  </Link>
                  <div className="h-5 w-px bg-border"></div>
                  <span className="text-sm font-medium text-muted-foreground">Super Admin</span>
                </div>
              ) : (
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-2 rounded-lg hover:bg-muted"
                >
                  <Menu size={24} />
                </button>
              )}
            </div>
            {sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="absolute right-0 top-1/2 p-2 rounded-full hover:bg-muted bg-background border border-border"
                style={{ transform: 'translate(50%, -50%)' }}
              >
                <ChevronLeft size={20} />
              </button>
            )}
          </div>

          {/* Dashboard başlığı ve sağ taraf */}
          <div className="flex items-center justify-between flex-1 pl-8 pr-4">
            <h1 className="text-xl font-medium">{pageTitle}</h1>

          <div className="flex items-center space-x-4">
            {/* Cihaz Hata Bildirimleri */}
            <div className="relative">
              <button
                onClick={() => {
                  setDeviceLogNotificationsOpen(!deviceLogNotificationsOpen)
                  setNotificationsOpen(false)
                }}
                className="p-2 rounded-lg hover:bg-muted relative"
                title="Cihaz Hataları"
              >
                <AlertTriangle size={20} className={unreadDeviceLogCount > 0 ? 'text-red-500' : ''} />
                {unreadDeviceLogCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold px-1">
                    {unreadDeviceLogCount > 9 ? '9+' : unreadDeviceLogCount}
                  </span>
                )}
              </button>

              <DeviceLogNotifications
                isOpen={deviceLogNotificationsOpen}
                onClose={() => setDeviceLogNotificationsOpen(false)}
              />
            </div>

            {/* Ticket Bildirimleri */}
            <div className="relative">
              <button
                onClick={() => {
                  setNotificationsOpen(!notificationsOpen)
                  setDeviceLogNotificationsOpen(false)
                }}
                className="p-2 rounded-lg hover:bg-muted relative"
                title="Destek Talepleri"
              >
                <Bell size={20} />
                {unreadTicketCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold px-1">
                    {unreadTicketCount > 9 ? '9+' : unreadTicketCount}
                  </span>
                )}
              </button>

              <TicketNotifications
                isOpen={notificationsOpen}
                onClose={() => setNotificationsOpen(false)}
              />
            </div>

            {/* Profil */}
            <div className="relative">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted"
              >
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <span>{user?.name || 'Kullanıcı'}</span>
                <ChevronDown size={16} />
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-card rounded-lg shadow-lg border border-border">
                  <div className="p-2">
                    <button 
                      onClick={logout} 
                      className="w-full text-left px-4 py-2 text-sm hover:bg-muted rounded-lg text-red-500"
                    >
                      Çıkış Yap
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </header>

      {/* Ana İçerik */}
      <main className={`fixed top-16 right-0 ${currentSong ? 'bottom-16' : 'bottom-0'} ${sidebarOpen ? 'left-64' : 'left-0'} transition-all duration-300 bg-background overflow-auto`}>
        {children}
      </main>
      
      {/* Global ses çalma motoru (görünmez) */}
      <AudioPlayer />
      
      {/* Spotify benzeri footer oynatıcı */}
      <GlobalFooterPlayer />
    </div>
  )
}

export default MainLayout
